import { DiffContent, Entity, Mutation, TrackingBuilder } from "../base";
import { Context } from "../base";
import { assertType } from "../base/utils";
import { CachingRef, IdentityRef, Ref } from './Ref';

class Currency implements Entity<Currency> {
    static STORE = "Currency";
    private _symbol:string;
    private _primary:boolean;
    private _uuid: string;

    constructor(uuid:string, symbol:string, primary:boolean) {
        assertType(uuid, "uuid", String);
        assertType(symbol, "symbol", String);
        assertType(primary, "primary", Boolean);
        this._uuid = uuid;
        this._symbol = symbol;
        this._primary = primary;
    }

    get uuid():string { return this._uuid;}
    get symbol ():string { return this._symbol; }
    get primary():boolean { return this._primary; }


    getRef() {
        return CachingRef.of(Currency, this._uuid);
    }

    builder() : CurrencyBuilder {
        return new CurrencyBuilder(this);
    }

    toJson():CurrencyJSON {
        return {
            uuid: this._symbol,
            symbol: this._symbol,
            primary: this._primary
        };
    }

    static identityValue = new Currency("identity", "", false);
    static identityRef = new IdentityRef(Currency);
    static identity() : Currency {
        return Currency.identityValue;
    }

    static fromUuid(uuid:string) : Currency | undefined {
        const finder = Context.getInstance().getFinder(Currency);
        return finder.findById(uuid);
    }

    static createRef(uuid: string) : Ref<Currency> {
        return CachingRef.of(Currency, uuid);
    }

    static fromJson(currency: CurrencyJSON) : Currency {
        return new Currency(currency.uuid, currency.symbol, currency.primary);
    }
}

class CurrencyBuilder extends TrackingBuilder<Currency> {
    private _uuid:string;
    private _symbol:string;
    private _primary:boolean;
    private _diff:CurrencyDiff = {};

    constructor(object:Currency|undefined) {
        super();
        const obj = object ? object : Currency.identity();
        this._uuid = obj.uuid;
        this._symbol = obj.symbol;
        this._primary = obj.primary;
    }

    uuid(uuid:string) {
        if (this.isTracking() && this._uuid !== uuid) {
            this._diff.uuid = uuid;
        }
        this._uuid = uuid;
        return this;
    }

    symbol(symbol:string) {
        if (this.isTracking() && this._symbol !== symbol) {
            this._diff.symbol = symbol;
        }
        this._symbol = symbol;
        return this;
    }

    primary(primary:boolean) {
        if (this.isTracking() && this._primary !== primary) {
            this._diff.primary = primary;
        }
        this._primary = primary;
        return this;
    }

    build(): Currency {
        return new Currency(this._uuid, this._symbol, this._primary);
    }

    applyDiff(diff: CurrencyDiff): CurrencyBuilder {
        if (diff.uuid) {
            this.uuid(diff.uuid);
        }
        if (diff.symbol) {
            this.symbol(diff.symbol);
        }
        if (diff.primary) {
            this.primary(diff.primary);
        }
        return this;
    }

    applyObj(object: Currency) : CurrencyBuilder {
        this.symbol(object.symbol);
        this.primary(object.primary);
        this.uuid(object.uuid);
        return this;
    }

    getDiff(): CurrencyDiff {
        return this._diff;
    }
}

interface CurrencyJSON {
    uuid: string,
    symbol:string,
    primary:boolean
}

interface CurrencyDiff extends DiffContent<Currency> {
    uuid?:string,
    symbol?:string,
    primary?:boolean
}

export {Currency, CurrencyJSON, CurrencyDiff, CurrencyBuilder}